import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrayParam, NumberParam, useQueryParams, withDefault } from 'use-query-params'

import { Flex, IllustratedMessage, Loader } from '@percent/lemonade'
import { Activity } from './Activity/Activity'
import { Filters } from './Filters/Filters'
import { useEmployeeActivityQuery } from './useEmployeeActivityQuery'

type EmployeeActivityProps = Readonly<{
  accountId: string
}>

export function EmployeeActivity({ accountId }: EmployeeActivityProps) {
  const { t } = useTranslation()

  const [queryParams, setQueryParams] = useQueryParams({
    pageSize: withDefault(NumberParam, 50),
    type: ArrayParam
  })

  const { data, isLoading, isFetching, fetchNextPage, isFetchingNextPage, hasNextPage, isError } =
    useEmployeeActivityQuery({
      accountId,
      pageSize: queryParams.pageSize,
      type: queryParams.type
    })

  const handleTypeFilterChange = type => {
    setQueryParams?.({
      type: !type?.length ? null : [type]
    })
  }

  const observerRef = useRef<IntersectionObserver | null>(null)

  const lastItemRef = useCallback(
    node => {
      if (isFetching || isFetchingNextPage) return

      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage()
        }
      })

      if (node) observerRef.current.observe(node)
    },
    [fetchNextPage, hasNextPage, isFetching, isFetchingNextPage]
  )

  const hasAnyActivity = !!data?.pages?.some(page => page.length > 0)
  const hasTypeFilter = !!queryParams.type?.length

  const hasNoData = !hasAnyActivity && !isFetchingNextPage && !hasNextPage

  if (isLoading) {
    return <Loader />
  }

  if (isError && !isFetchingNextPage) {
    return <IllustratedMessage illustration="general-error" title={t('errorMessage.genericError')} />
  }

  return (
    <Flex direction="column" style={{ minHeight: '20rem' }}>
      {hasAnyActivity || hasTypeFilter || hasNextPage ? (
        <Filters queryParams={queryParams} handleTypeFilterChange={handleTypeFilterChange} />
      ) : null}

      {hasNoData && (
        <IllustratedMessage
          illustration="nothing-to-see"
          title={t('workplaceGiving.employeeActivity.noActivity.title')}
          description={t('workplaceGiving.employeeActivity.noActivity.description')}
        />
      )}

      <div>{data?.pages.map(p => p.map(activity => <Activity key={activity.date} activity={activity} />))}</div>

      {isFetchingNextPage ? (
        <div data-testid="next-page-loader" style={{ position: 'relative', padding: '1rem' }}>
          <Loader />
        </div>
      ) : null}
      {hasAnyActivity || hasNextPage ? <div data-testid="end-of-list-observer" ref={lastItemRef} /> : null}
    </Flex>
  )
}
